import React from 'react';
import ExpandingInfo from './components/calculators-layout/ExpandingInfo';
import CalculatorForm from './components/calculators-layout/CalculatorForm';
import CalculatorLayout from './components/calculators-layout/CalculatorLayout';
import {postBorrowAmount} from './api';
import InputWithLabel from '../LoanApplication/components/commons/InputWithLabel';
import RepaymentYear from '../../components/RepaymentYear';
import CalculatorResultCard from './components/calculators-layout/CalculatorResultCard';
import CalculatorCallToActionCard from './components/calculators-layout/CalculatorCallToActionCard';
import BorrowerButton from './components/BorrowAmount/BorrowerButton';
import PlusMinusButton from '../../components/PlusMinusButton';
import RepaymentFrequency from './components/BorrowAmount/RepaymentFrequency';
import CollapseButton from '../../components/CollapseButton';
import ReusableGraph from './components/ReusableGraph/ReusableGraph';
import reusableLineGraphConfig from './components/ReusableGraph/reusableLineGraphConfig';
import CalculatorAssumptions from './components/calculators-layout/CalculatorAssumptions';
import BorrowingCapacityAssumptionContent from './AssumptionBoxContent/BorrowingCapacityAssumptionContent';
import currencyFormatter from '../../utils/currencyFormatter';

const interestRate = I18n.t('calculators.interest_matrix.live.regular.payg_variable')[0];

class BorrowingCapacityCalculator extends React.Component {
  state = {
    incomeType: 0,
    numberOfBorrowers: '1',
    numberOfDependants: 0,
    borrower1Salary: 150000,
    borrower1Frequency: '1',
    borrower2Salary: null,
    borrower2Frequency: '1',
    rentalIncome: null,
    rentalFrequency: '52',
    livingExpenses: 2500,
    creditCardLimit: null,
    otherLoanRepayments: null,
    interestRate,
    loanTerm: 30,
    repaymentFrequency: 'monthly',
    errors: {},
    houseYouCanAfford: null,
    results: {
      RepaymentMonthlyString: '',
      RepaymentFortnightlyString: '',
      RepaymentWeeklyString: '',
      interestRate,
      lineGraphData: [],
      loanTerm: 30
    }
  };
  
  salaryFrequencyOptions = [
    { value: "1", label: "Yearly" },
    { value: "12", label: "Monthly" },
    { value: "26", label: "Fortnightly"},
    { value: "52", label: "Weekly"}
  ];
  
  frequencyOptions = [
    { value: "1", label: "Yearly" },
    { value: "12", label: "Monthly" },
    { value: "26", label: "Fortnightly"},
    { value: "52", label: "Weekly"}
  ];

  getBorrowingPower = async () => {
    const {
      incomeType,
      numberOfDependants,
      numberOfBorrowers,
      interestRate,
      loanTerm,
      borrower1Salary,
      borrower1Frequency,
      borrower2Salary,
      borrower2Frequency,
      rentalIncome,
      rentalFrequency,
      livingExpenses,
      creditCardLimit,
      otherLoanRepayments,
    } = this.state;

    const jointIncome = numberOfBorrowers === 1 ? 0 : 1;
    const limitNumberOfDependents = numberOfDependants > 3 ? 3 : numberOfDependants

    const inputValues = {
      ClientID: 'Yard',
      Token: gon.services.calculator_api_key,
      TestDomain: 'yard.com.au',
      CalcName: 'BorrowingPower',
      JointIncome: jointIncome,  // 1 yes, 0 no
      NoOfDependents: limitNumberOfDependents,
      InterestRate: interestRate,
      LoanTerm: loanTerm,
      IncomeType: incomeType,        //1 -- Net Income  0 -- Gross Income
      NetSalary1: borrower1Salary,
      NetSalaryFre1: borrower1Frequency,  // 1 - yearly, 2 -half yearly, 12 - monthly, 26 - fortnightly
      NetSalary2: borrower2Salary,
      NetSalaryFre2: borrower2Frequency,   // 1 - yearly, 2 -half yearly, 12 - monthly, 26 - fortnightly
      NetSalary3: rentalIncome,
      NetSalaryFre3: rentalFrequency,  // 1 - yearly, 2 -half yearly, 12 - monthly, 26 - fortnightly
      Expense1: livingExpenses,   //Living expenses 
      Expense2: '0',     //Car loan repayment
      Expense3: creditCardLimit,   //Total credit card limits
      Expense4: otherLoanRepayments,       //Other payments
      Expense1Fre:'12',     // 1 - yearly, 2 -half yearly, 12 - monthly, 26 - fortnightly
      Expense2Fre: '12',   // 1 - yearly, 2 -half yearly, 12 - monthly, 26 - fortnightly
      Expense4Fre: '12',    // 1 - yearly, 2 -half yearly, 12 - monthly, 26 - fortnightly
      SettingBufferRate: '2.5'
    };

    const input = JSON.stringify(inputValues);
    try {
      const response = await fetch('https://www.visionabacus.net/API/Yard/borrowingpower/json', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: input,
      });

      const data = await response.json();
      const lineGraphData = this.lineGraphCalculator(data)
      const loanAmount = parseInt(data.LoanAmountString.replace(/[$,]/g, ""));
      data.interestRate = this.state.interestRate;
      data.loanTerm = loanTerm;
      data.lineGraphData = lineGraphData;

      this.setState({ results: data, houseYouCanAfford: currencyFormatter.format((loanAmount / 0.80).toFixed(0)) });
    } catch (error) {
      console.error('An error occurred...', error);
    }
  };

  repaymentPeriodsPerYear = (repaymentFrequency) => {
    switch (repaymentFrequency) {
      case 'monthly':
        return 12
      case 'fortnightly':
        return 26
      case 'weekly':
        return 52
    }
  }

  periodicRepaymentFor = (data, repaymentFrequency) => {
    const repaymentByFrequency = {
      monthly: data.RepaymentMonthlyString,
      fortnightly: data.RepaymentFortnightlyString,
      weekly: data.RepaymentWeeklyString,
    }

    return parseFloat(repaymentByFrequency[repaymentFrequency].replace(/[$,]/g, ""))
  }

  // VisionAbacus returns currency-rounded repayments. Cap each principal
  // payment at the outstanding balance so those rounded values cannot make the
  // graph's remaining balance negative near the end of the loan term.
  lineGraphCalculator = (data) => {
    const { interestRate, loanTerm, repaymentFrequency } = this.state;
    const borrowingCapacityAmount = parseInt(data.LoanAmountString.replace(/[$,]/g, ""));

    const periodsPerYear = this.repaymentPeriodsPerYear(repaymentFrequency);
    const totalPeriods = periodsPerYear * loanTerm;
    const periodicRate = (interestRate / 100) / periodsPerYear;
    const periodicRepayment = this.periodicRepaymentFor(data, repaymentFrequency);

    let principalOwed = borrowingCapacityAmount;
    let cumulativeInterest = 0;
    let cumulativePrincipal = 0;

    let lineGraphData = [
      {
        year: 0,
        interest: 0,
        principal: 0,
        remaining: borrowingCapacityAmount,
        max_y_axis: parseInt((borrowingCapacityAmount * 1.1).toFixed(0)),
      },
    ]

    for (let period = 1; period <= totalPeriods; period++) {
      const paidInterest = principalOwed * periodicRate
      const scheduledPrincipal = periodicRepayment - paidInterest
      const paidPrincipal = Math.min(scheduledPrincipal, principalOwed)
      principalOwed = Math.max(0, principalOwed - paidPrincipal)
      cumulativeInterest += paidInterest
      cumulativePrincipal += paidPrincipal

      if (period % periodsPerYear !== 0) continue;

      lineGraphData.push({
        year: period / periodsPerYear,
        interest: Math.round(cumulativeInterest),
        principal: Math.round(cumulativePrincipal),
        remaining: Math.round(principalOwed),
      });
    }
    return lineGraphData;
  }
  
  onCalculationSuccess = (response) => {
    //this.setState({results: response.data})
  };

  onCalculationFailure = ({ response }) => {
    let errors = response.data.errors;

    let swalTitle = I18n.t('calculators.generic_error.title');
    let swalText = I18n.t('calculators.generic_error.description');

    if ((response.status) == 422) {
      swalTitle = I18n.t('calculators.generic_error.title');
      swalText = response.data.errors
    }

    swal({
      title: swalTitle,
      text: swalText,
      type: 'warning',
      showCancelButton: false,
      confirmButtonText: 'Close',
      allowOutsideClick: false,
      confirmButtonColor: "#10c98f",
    })
  };

  onCalculationSubmit = () => {
    let data = {
      borrow_amount: {...this.state, interestRate: this.state.interestRate * 0.01}
    };

    this.getBorrowingPower();
    //postBorrowAmount(data, this.onCalculationSuccess, this.onCalculationFailure)
  };

  componentDidMount = () => {
    this.onCalculationSubmit()
  };

  handleNumberOfDependants = (value) => {
    let numberOfDependants = this.state.numberOfDependants
    if ((value == 'plus') && numberOfDependants < 10) {
      numberOfDependants++
    } else if ((value == 'minus') && numberOfDependants > 0) {
      numberOfDependants--
    }
    this.setState({ numberOfDependants })
  };

  rentalIncomeLabel = () => {
    return this.state.numberOfBorrowers === 2 ? 'Combined rent income' : 'Rent'
  };

  repaymentValue = (repayment) => {
    const { repaymentFrequency, results } = this.state;
    switch (repaymentFrequency) {
      case 'monthly':
        return results.RepaymentMonthlyString.split('.')[0];
        break;
      case 'fortnightly':
        return results.RepaymentFortnightlyString.split('.')[0];
        break;
      case 'weekly':
        return results.RepaymentWeeklyString.split('.')[0];
        break;
    }
  }
  
  expandingLead = (
    <p>Are you trying to determine what property you can afford? This calculator will help you estimate your home loan borrowing capacity, the value of the home you can afford (assuming you are buying with a 20% deposit) and your monthly repayment.</p>
  );
  expandingInfoContent = (
    <React.Fragment>
      <p>To estimate your borrowing capacity, you should enter the number of borrowers, i.e. whether you will be applying for the home loan by yourself or with someone else (e.g. spouse, partner).</p>
      <p>Then you will be required to enter the net salary income for each applicant and rent income if applicable. </p>
      <p>You should also provide an estimate of your monthly living expenses. You will be required to also enter the value of your credit card limits and minimum monhtly repayments for other loans you currently have (e.g. car loans, personal loans, HECS/HELP). </p>
      <p>Last but not least, you are required to enter an indicative loan interest rate, loan term and your desired repayment frequency.</p>
      <p>Based on the above information, the calculator will provide an estimated maximum loan amount and loan repayment.</p>
    </React.Fragment>
  );

  calculatorForm() {
    const {
      numberOfBorrowers,
      numberOfDependants,
      borrower1Salary,
      borrower1Frequency,
      borrower2Salary,
      borrower2Frequency,
      rentalIncome,
      rentalFrequency,
      livingExpenses,
      creditCardLimit,
      otherLoanRepayments,
      interestRate,
      loanTerm,
      errors
    } = this.state;
    
    return (
      <React.Fragment>
        <div className="input-aligned-with-label">
          <p className="input-aligned-with-label__label">How many borrowers?</p>
          <div className="input-aligned-with-label__input borrower-button calculator-toggle-group compact">
            <BorrowerButton
              onClick={ (value) => {this.setState({
                numberOfBorrowers: value,
                borrower1Salary: borrower1Salary,
                borrower2Salary: borrower2Salary,
              })}}
            />
          </div>
        </div>
        <div className="input-aligned-with-label">
          <p className="input-aligned-with-label__label">How many dependants?</p>
          <div className="input-aligned-with-label__input plus-minus-button">
            <PlusMinusButton
              numberOfDependants={numberOfDependants}
              handleNumberOfDependants={(value) => this.handleNumberOfDependants(value)}
            />
          </div>
        </div>
        <div className="calculators-divider" />
        <div>
          <p className='calculators-form-section-heading'><strong className='text-uppercase'>Your Income</strong></p>
          <label>Income type</label>
          <div className="input-aligned-with-label__gross_net borrower-button calculator-toggle-group compact mb-3">
            <BorrowerButton
              width={'income-type-btn'}
              label1={'Gross'}
              label2={'Net'}
              value1={0}
              value2={1}
              onClick={ (value) => {this.setState({
                incomeType: value,
              })}}
            />
          </div>
          {
            numberOfBorrowers === '1' ? (
              <div className="combined-input">
                <InputWithLabel
                  label='Salary'
                  type='currency'
                  defaultValue={borrower1Salary}
                  onChange={(borrower1Salary) => this.setState({borrower1Salary})}
                />
                <InputWithLabel type='select'
                                defaultValue={borrower1Frequency}
                                options={ this.salaryFrequencyOptions }
                                onChange={(value) => this.setState({borrower1Frequency: value })} />
              </div>
            ) : (
              <React.Fragment>
                <div className="combined-input">
                  <InputWithLabel
                    label='Borrower 1 Salary'
                    type='currency'
                    defaultValue={borrower1Salary}
                    onChange={(value) => this.setState({borrower1Salary: value})}
                  />
                  <InputWithLabel type='select'
                                  defaultValue={borrower1Frequency}
                                  options={ this.salaryFrequencyOptions }
                                  onChange={(value) => this.setState({borrower1Frequency: value })} />
                </div>
                <div className="combined-input">
                  <InputWithLabel
                    label='Borrower 2 Salary'
                    type='currency'
                    defaultValue={borrower2Salary}
                    onChange={(value) => this.setState({borrower2Salary: value})}
                  />
                  <InputWithLabel type='select'
                                  defaultValue={borrower2Frequency}
                                  options={ this.salaryFrequencyOptions }
                                  onChange={(value) => this.setState({borrower2Frequency: value })} />
                </div>
              </React.Fragment>
            )
          }
        </div>
        <CollapseButton
          id='rent'
          openLabel='+ Add rental income'
          closeLabel='- Remove rental income'
          onCollapse={() => {this.setState({rentalIncome: null})}}
        >
          <div className="combined-input">
            <InputWithLabel
              type='currency'
              label={this.rentalIncomeLabel()}
              defaultValue={rentalIncome || ''}
              onChange={(value) => {this.setState({rentalIncome: value})}}
            />
            <InputWithLabel type='select'
                            defaultValue={rentalFrequency}
                            options={ this.frequencyOptions }
                            onChange={(value) => this.setState({rentalFrequency: value })} />
          </div>
        </CollapseButton>
        <div className="calculators-divider" />
        <p className='calculators-form-section-heading'><strong className='text-uppercase'>Your monthly expenses</strong></p>
        <div className="numeric-input">
          <InputWithLabel
            label='Living expenses'
            type='currency'
            defaultValue={livingExpenses}
            errorMessage={errors['livingExpenses']}
            onChange={(value) => this.setState({livingExpenses: value})}
          />
        </div>
        <CollapseButton
          id='credit-card-limit'
          openLabel='+ Add credit card limit'
          closeLabel='- Remove credit card limit'
          onCollapse={() => {this.setState({creditCardLimit: null})}}
        >
          <div className="numeric-input">
            <InputWithLabel
              label='Credit card limit'
              type='currency'
              defaultValue={creditCardLimit}
              errorMessage={errors['creditCardLimit']}
              onChange={(value) => this.setState({creditCardLimit: value})}
            />
          </div>
        </CollapseButton>
        <CollapseButton
          id='other-loan-repayment'
          openLabel='+ Add other loan repayment'
          closeLabel='- Remove other loan repayment'
          onCollapse={() => {this.setState({otherLoanRepayments: null})}}
        >
          <div className="numeric-input">
            <InputWithLabel
              label='Other loan repayment'
              type='currency'
              defaultValue={otherLoanRepayments}
              errorMessage={errors['otherLoanRepayments']}
              onChange={(value) => this.setState({otherLoanRepayments: value})}
            />
          </div>
        </CollapseButton>
        <div className="calculators-divider" />
        <div className="rate-input">
          <InputWithLabel
            label='Interest rate'
            type='append'
            defaultValue={interestRate}
            appendText={ '%' }
            errorMessage={errors['interestRate']}
            onChange={(value) => this.setState({interestRate: value})}
          />
        </div>
        <RepaymentYear
          label='Loan term (years)'
          defaultValue={loanTerm}
          max={40}
          marks={{
            0:  { style: { fontSize: '17px' }, label: 0 },
            5:  { style: { fontSize: '17px' }, label: 5 },
            10: { style: { fontSize: '17px' }, label: 10 },
            15: { style: { fontSize: '17px' }, label: 15 },
            20: { style: { fontSize: '17px' }, label: 20 },
            25: { style: { fontSize: '17px' }, label: 25 },
            30: { style: { fontSize: '17px' }, label: 30 },
            35: { style: { fontSize: '17px' }, label: 35 },
            40: { style: { fontSize: '17px' }, label: 40 },
          }}
          onChange={(value) => this.setState({loanTerm: value})}
        />
        <div className='borrowing-capacity-calculator__repayment-frequency'>
          <div className='borrowing-capacity-calculator__repayment-frequency calculator-toggle-group triple'>
            <RepaymentFrequency
              onClick={(value) => this.setState({repaymentFrequency: value})}/>
          </div>
        </div>
      </React.Fragment>
    )
  }

  resultsColumn() {
    const {results, repaymentFrequency, houseYouCanAfford} = this.state;
    const graphProps = {
      graphData: results.lineGraphData,
      loanTerm: results.loanTerm,
      ...reusableLineGraphConfig
    }
    return results ? <React.Fragment>
      <CalculatorResultCard
        light
        caption='You can borrow up to'
        figure={results.LoanAmountString}
        additionalInfo={(
          <p>Which means you can afford a house up to {houseYouCanAfford}</p>
        )}
      />
      <CalculatorResultCard
        caption='You would be repaying'
        figure={this.repaymentValue()}
        figureText={repaymentFrequency}
        additionalInfo={(
          <p>At an interest rate of {parseFloat(results.interestRate).toFixed(2)}% over a {results.loanTerm} year term</p>
        )}
      />
      <CalculatorCallToActionCard>
        <div className='borrowing-capacity-calculator__cta'>
          <p className='borrowing-capacity-calculator__cta-text regular-cta'>Need pre-approval?</p>
        </div>
      </CalculatorCallToActionCard>
      <ReusableGraph graphProps={graphProps} />
    </React.Fragment> : null;
  }

  render() {
    return (
      <div className="borrowing-capacity-calculator">
        <CalculatorLayout
          infoRow={<ExpandingInfo lead={this.expandingLead}>{this.expandingInfoContent}</ExpandingInfo>}
          formColumn={
            <CalculatorForm handleSubmit={this.onCalculationSubmit}>{this.calculatorForm()}</CalculatorForm>
          }
          resultsColumn={this.resultsColumn()}
        >
          <CalculatorAssumptions assumptionBoxContents={<BorrowingCapacityAssumptionContent />} />
        </CalculatorLayout>
      </div>
    );
  }
}

export default BorrowingCapacityCalculator;
